import { useState } from "react";
import PropTypes from "prop-types";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { Link, useSearchParams } from "react-router-dom";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useCan } from "auth/AuthContext";
import { PERMISSIONS } from "auth/access";
import { formatDate, formatNumber } from "utils/format";

const DUTIES = [
  { value: 1, label: "Phó bang" },
  { value: 2, label: "Trưởng lão" },
  { value: 3, label: "Thành viên" },
];

const CLAN_FIELDS = [
  ["name", "Tên bang"],
  ["lvl", "Cấp"],
  ["fund", "Quỹ"],
  ["exp", "Kinh nghiệm bang"],
  ["stone", "Linh thạch"],
  ["soul", "Hồn binh"],
];

function EditClanDialog({ clan, onClose, onDone }) {
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [form, setForm] = useState({
    name: clan.name,
    slogan: clan.slogan || "",
    fund: clan.fund,
    lvl: clan.lvl,
    exp: clan.exp,
    stone: clan.stone,
    soul: clan.soul,
  });
  const field = (key, label, props = {}) => (
    <Grid item xs={12} sm={6}>
      <MDInput
        fullWidth
        label={label}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        {...props}
      />
    </Grid>
  );
  const changes = [
    ...CLAN_FIELDS.filter(([key]) => String(form[key]) !== String(clan[key] ?? "")).map(
      ([key, label]) =>
        key === "name"
          ? `• ${label}: "${clan.name}" → "${form.name}"`
          : `• ${label}: ${formatNumber(clan[key])} → ${formatNumber(Number(form[key]) || 0)}`
    ),
    form.slogan !== (clan.slogan || "") && "• Khẩu hiệu",
  ].filter(Boolean);

  const submit = async () => {
    const lvlDown = Number(form.lvl) < clan.lvl;
    const yes = await confirm({
      title: `Lưu bang ${clan.name}`,
      message: [
        ...changes,
        "",
        lvlDown &&
          "⚠ Hạ cấp bang: số thành viên và chức vụ tối đa có thể giảm theo cấp mới — kiểm tra lại trước khi lưu.",
        "Có hiệu lực ngay cho mọi thành viên và được ghi vào nhật ký bang.",
      ]
        .filter((l) => l !== false)
        .join("\n"),
      color: lvlDown ? "error" : "warning",
      confirmText: "Lưu",
    });
    if (!yes) return;
    const { ok } = await run(
      () =>
        api.post(`/api/admin/clans/${clan.clanId}/update`, {
          name: form.name,
          slogan: form.slogan,
          fund: Number(form.fund),
          lvl: Number(form.lvl),
          exp: Number(form.exp),
          stone: Number(form.stone),
          soul: Number(form.soul),
        }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Sửa bang ${clan.name}`}
      description="Sửa trực tiếp thông tin bang; có hiệu lực ngay cho mọi thành viên."
      color="warning"
      submitText={changes.length ? "Lưu…" : "Chưa có thay đổi"}
      disabled={changes.length === 0}
      busy={busy}
      maxWidth="sm"
      onClose={onClose}
      onSubmit={submit}
    >
      <Grid container spacing={2}>
        {field("name", "Tên bang", {
          helperText: "Chữ thường không dấu và số, 5 - 20 ký tự",
        })}
        {field("lvl", `Cấp (1 - ${clan.maxLvl})`, {
          type: "number",
          inputProps: { min: 1, max: clan.maxLvl },
        })}
        {field("fund", "Quỹ", { type: "number", inputProps: { min: 0 } })}
        {field("exp", "Kinh nghiệm bang ở cấp hiện tại", {
          helperText: "Đủ kinh nghiệm thì bang tự lên cấp",
          type: "number",
          inputProps: { min: 0 },
        })}
        {field("stone", "Linh thạch", { type: "number", inputProps: { min: 0 } })}
        {field("soul", "Hồn binh", { type: "number", inputProps: { min: 0 } })}
        <Grid item xs={12}>
          <MDInput
            fullWidth
            multiline
            minRows={2}
            label="Khẩu hiệu"
            value={form.slogan}
            onChange={(e) => setForm({ ...form, slogan: e.target.value })}
          />
        </Grid>
      </Grid>
    </ActionDialog>
  );
}

EditClanDialog.propTypes = {
  clan: PropTypes.shape({
    clanId: PropTypes.number,
    name: PropTypes.string,
    slogan: PropTypes.string,
    fund: PropTypes.number,
    lvl: PropTypes.number,
    maxLvl: PropTypes.number,
    exp: PropTypes.number,
    stone: PropTypes.number,
    soul: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function DutyDialog({ clanId, member, onClose, onDone }) {
  const [run, busy] = useRunner();
  const [duty, setDuty] = useState(member.duty === 0 ? 3 : member.duty);
  const submit = async () => {
    const { ok } = await run(
      () => api.post(`/api/admin/clans/${clanId}/duty`, { userId: member.userId, duty }),
      (text) => text
    );
    if (ok) {
      onClose();
      onDone();
    }
  };
  return (
    <ActionDialog
      open
      title={`Đổi chức vụ ${member.name}`}
      submitText="Lưu"
      busy={busy}
      disabled={duty === member.duty}
      onClose={onClose}
      onSubmit={submit}
    >
      <MDInput
        select
        label="Chức vụ"
        value={duty}
        onChange={(e) => setDuty(e.target.value)}
        SelectProps={{ sx: { height: 44 } }}
      >
        {DUTIES.map((d) => (
          <MenuItem key={d.value} value={d.value}>
            {d.label}
          </MenuItem>
        ))}
      </MDInput>
      <Alert severity="info">
        Số phó bang / trưởng lão tối đa tuỳ cấp bang; đã đủ người thì máy chủ báo lỗi. Muốn đổi bang
        chủ thì dùng nút &quot;Làm bang chủ&quot;.
      </Alert>
    </ActionDialog>
  );
}

DutyDialog.propTypes = {
  clanId: PropTypes.number.isRequired,
  member: PropTypes.shape({
    userId: PropTypes.number,
    name: PropTypes.string,
    duty: PropTypes.number,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDone: PropTypes.func.isRequired,
};

function ClanDetailDialog({ clanId, onClose, onChanged }) {
  const canEdit = useCan(PERMISSIONS.CLAN_EDIT);
  const canPlayer = useCan(PERMISSIONS.PLAYER_VIEW);
  const confirm = useConfirm();
  const [run, busy] = useRunner();
  const detail = useFetch(() => api.get(`/api/admin/clans/${clanId}`), [clanId]);
  const log = useFetch(() => api.get(`/api/admin/clans/${clanId}/log`), [clanId]);
  const [editing, setEditing] = useState(false);
  const [dutyMember, setDutyMember] = useState(null);
  const clan = detail.data;

  const reload = () => {
    detail.reload(true);
    log.reload(true);
    onChanged();
  };

  const memberAction = async (path, title, message, color) => {
    if (!(await confirm({ title, message, color, confirmText: title }))) return;
    const { ok } = await run(
      () => api.post(path.url, path.body),
      (text) => text
    );
    if (ok) reload();
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {clan ? `Bang ${clan.name}` : "Bang"}
        <MDTypography component="span" variant="caption" color="text" ml={1}>
          #{clanId}
        </MDTypography>
      </DialogTitle>
      <DialogContent>
        {detail.error && <Alert severity="error">{detail.error}</Alert>}
        {clan && (
          <>
            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1}>
              <Chip color="info" label={`Cấp ${clan.lvl}/${clan.maxLvl}`} />
              <Chip
                label={
                  clan.nextLvl
                    ? `EXP ${formatNumber(clan.exp)}/${formatNumber(clan.expNeed)}`
                    : "Cấp tối đa"
                }
              />
              <Chip label={`Quỹ ${formatNumber(clan.fund)}`} />
              <Chip label={`Linh thạch ${formatNumber(clan.stone)}`} />
              <Chip label={`Hồn binh ${formatNumber(clan.soul)}`} />
              <Chip label={`${clan.members.length}/${clan.maxMember} thành viên`} />
              {clan.nextLvl && (
                <Chip
                  variant="outlined"
                  label={`Lên cấp ${clan.nextLvl.lvl}: tối đa ${clan.nextLvl.maxMember} thành viên`}
                />
              )}
              {canEdit && (
                <MDButton size="small" color="warning" onClick={() => setEditing(true)}>
                  <Icon>edit</Icon>&nbsp;Sửa bang
                </MDButton>
              )}
            </MDBox>
            {(clan.building || clan.boss) && (
              <MDBox display="flex" gap={1} flexWrap="wrap" mb={1}>
                {clan.building && (
                  <Chip
                    color="warning"
                    label={`Đang xây ${clan.building.name} cấp ${
                      clan.building.to
                    }, xong ${formatDate(clan.building.finishAt)}`}
                  />
                )}
                {clan.boss && (
                  <Chip
                    color="error"
                    label={`Boss ${clan.boss.name} (${clan.boss.difficultyName}): mất ${
                      clan.boss.percentLost
                    }% máu, ${clan.boss.fighters} người đang đánh, hết lúc ${formatDate(
                      clan.boss.expireAt
                    )}`}
                  />
                )}
              </MDBox>
            )}
            <MDTypography variant="caption" color="text" display="block" mb={0.5}>
              Công trình, binh khí, nghiên cứu
            </MDTypography>
            <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
              {(clan.buildings || []).map((b) => (
                <Chip key={`b${b.id}`} variant="outlined" label={`${b.name} ${b.lvl}`} />
              ))}
              {(clan.weapons || [])
                .filter((w) => w.lvl > 0)
                .map((w) => (
                  <Chip
                    key={`w${w.id}`}
                    variant="outlined"
                    label={`${w.name} ${w.lvl} (+${w.percent}%)`}
                  />
                ))}
              {(clan.research || []).map((k) => (
                <Chip key={`r${k.id}`} variant="outlined" label={`${k.name} ${k.lvl}`} />
              ))}
            </MDBox>
            {clan.slogan && (
              <MDTypography variant="button" color="text" display="block" mb={2}>
                Khẩu hiệu: {clan.slogan}
              </MDTypography>
            )}
            <MDTypography variant="h6">Thành viên</MDTypography>
            <AdminTable
              rows={clan.members}
              rowKey="userId"
              emptyText="Không có thành viên"
              columns={[
                {
                  key: "name",
                  label: "Nhân vật",
                  render: (m) => (
                    <MDBox display="flex" gap={1} alignItems="center">
                      {canPlayer ? (
                        <MDTypography
                          component={Link}
                          to={`/players/${m.userId}`}
                          variant="button"
                          color="info"
                        >
                          {m.name}
                        </MDTypography>
                      ) : (
                        m.name
                      )}
                      {m.online && <Chip size="small" color="success" label="Online" />}
                    </MDBox>
                  ),
                },
                {
                  key: "duty",
                  label: "Chức vụ",
                  render: (m) => (
                    <Chip
                      size="small"
                      color={m.duty === 0 ? "error" : "default"}
                      label={m.dutyName}
                    />
                  ),
                },
                canEdit && {
                  key: "actions",
                  label: "",
                  render: (m) =>
                    m.duty !== 0 && (
                      <MDBox display="flex" gap={0.5}>
                        <MDButton
                          size="small"
                          variant="text"
                          color="info"
                          disabled={busy}
                          onClick={() => setDutyMember(m)}
                        >
                          Đổi chức
                        </MDButton>
                        <MDButton
                          size="small"
                          variant="text"
                          color="warning"
                          disabled={busy}
                          onClick={() =>
                            memberAction(
                              {
                                url: `/api/admin/clans/${clanId}/leader`,
                                body: { userId: m.userId },
                              },
                              "Đổi bang chủ",
                              [
                                `Cho ${m.name} làm bang chủ bang ${clan.name}?`,
                                "",
                                `• Bang chủ hiện tại (${
                                  clan.members.find((x) => x.duty === 0)?.name || "?"
                                }) thành thành viên thường.`,
                                `• ${m.name} nhận thông báo trong game nếu đang online.`,
                              ].join("\n"),
                              "warning"
                            )
                          }
                        >
                          Làm bang chủ
                        </MDButton>
                        <MDButton
                          size="small"
                          variant="text"
                          color="error"
                          disabled={busy}
                          onClick={() =>
                            memberAction(
                              {
                                url: `/api/admin/clans/${clanId}/kick`,
                                body: { userId: m.userId },
                              },
                              "Đuổi khỏi bang",
                              [
                                `Đuổi ${m.name} khỏi bang ${clan.name}?`,
                                "",
                                `• Mất ${formatNumber(
                                  m.contribution
                                )} cống hiến đang có trong bang.`,
                                "• Phải chờ 24 giờ mới vào được bang (kể cả bang này).",
                                "• Đang trong bang chiến thì máy chủ sẽ không cho đuổi.",
                              ].join("\n"),
                              "error"
                            )
                          }
                        >
                          Đuổi
                        </MDButton>
                      </MDBox>
                    ),
                },
                {
                  key: "contribution",
                  label: "Cống hiến đang có",
                  align: "right",
                  render: (m) => formatNumber(m.contribution),
                },
                {
                  key: "weekContribution",
                  label: "Tuần này",
                  align: "right",
                  render: (m) => formatNumber(m.weekContribution),
                },
                {
                  key: "lifetimeContribution",
                  label: "Trọn đời",
                  align: "right",
                  render: (m) => formatNumber(m.lifetimeContribution),
                },
                {
                  key: "donateToday",
                  label: "Góp hôm nay",
                  render: (m) =>
                    `${m.donateCoinToday} ngọc · ${m.donateGoldToday} vàng · ${formatNumber(
                      m.donatePremiumGoldToday
                    )} vàng cao cấp`,
                },
                { key: "timeJoin", label: "Vào bang", render: (m) => formatDate(m.timeJoin) },
                {
                  key: "lastOnline",
                  label: "Online lần cuối",
                  render: (m) => (m.online ? "Đang online" : formatDate(m.lastOnline)),
                },
              ].filter(Boolean)}
            />
            <Grid container spacing={2} mt={1}>
              <Grid item xs={12} md={8}>
                <MDTypography variant="h6">Nhật ký bang</MDTypography>
                {log.error && <Alert severity="error">{log.error}</Alert>}
                <AdminTable
                  rows={log.data || []}
                  rowKey="id"
                  emptyText="Chưa có nhật ký"
                  columns={[
                    { key: "time", label: "Lúc", render: (r) => formatDate(r.time) },
                    { key: "userName", label: "Người", render: (r) => r.userName || "Hệ thống" },
                    { key: "detail", label: "Nội dung" },
                  ]}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MDTypography variant="h6">Xin vào bang</MDTypography>
                <AdminTable
                  rows={clan.joinRequests}
                  rowKey="userId"
                  emptyText="Không có"
                  columns={[
                    {
                      key: "name",
                      label: "Nhân vật",
                      render: (r) => <Link to={`/players/${r.userId}`}>{r.name}</Link>,
                    },
                    { key: "time", label: "Lúc", render: (r) => formatDate(r.time) },
                  ]}
                />
              </Grid>
            </Grid>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="secondary" onClick={onClose}>
          Đóng
        </MDButton>
      </DialogActions>
      {editing && clan && (
        <EditClanDialog clan={clan} onClose={() => setEditing(false)} onDone={reload} />
      )}
      {dutyMember && (
        <DutyDialog
          clanId={clanId}
          member={dutyMember}
          onClose={() => setDutyMember(null)}
          onDone={reload}
        />
      )}
    </Dialog>
  );
}

ClanDetailDialog.propTypes = {
  clanId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func.isRequired,
};

function Clans() {
  const [params, setParams] = useSearchParams();
  // ?clan=<id> mở thẳng chi tiết bang (link từ trang người chơi / gửi cho người khác).
  const selected = Number(params.get("clan")) || null;
  const setSelected = (id) => setParams(id ? { clan: id } : {}, { replace: true });
  const [q, setQ] = useState("");
  const [applied, setApplied] = useState("");
  const [sort, setSort] = useState("lvl");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(50);
  const list = useFetch(
    () => api.get("/api/admin/clans", { q: applied, sort, page, size }),
    [applied, sort, page, size]
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="clans" />
        <PageCard title="Bang hội" subtitle="Bấm vào một bang để xem chi tiết" noPadding>
          <MDBox
            px={2}
            pb={1}
            component="form"
            display="flex"
            gap={2}
            flexWrap="wrap"
            alignItems="center"
            onSubmit={(e) => {
              e.preventDefault();
              setPage(1);
              setApplied(q);
            }}
          >
            <MDInput
              label="Tên bang hoặc tên bang chủ"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <MDInput
              select
              label="Sắp xếp"
              value={sort}
              onChange={(e) => {
                setSort(e.target.value);
                setPage(1);
              }}
              SelectProps={{ sx: { height: 44 } }}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="lvl">Cấp cao nhất</MenuItem>
              <MenuItem value="fund">Quỹ nhiều nhất</MenuItem>
              <MenuItem value="members">Đông thành viên</MenuItem>
              <MenuItem value="id">Lập sớm nhất</MenuItem>
            </MDInput>
            <MDButton type="submit" variant="gradient" color="info">
              Tìm
            </MDButton>
          </MDBox>
          {list.error && <Alert severity="error">{list.error}</Alert>}
          <AdminTable
            loading={list.loading}
            rows={list.data?.items || []}
            rowKey="clanId"
            page={page}
            size={size}
            total={list.data?.total ?? 0}
            onPageChange={setPage}
            onSizeChange={(s) => {
              setSize(s);
              setPage(1);
            }}
            onRowClick={(c) => setSelected(c.clanId)}
            emptyText="Không có bang nào"
            columns={[
              {
                key: "name",
                label: "Bang",
                render: (c) => (
                  <MDBox lineHeight={1.2}>
                    <MDTypography variant="button" fontWeight="medium" display="block">
                      {c.name}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      #{c.clanId}
                    </MDTypography>
                  </MDBox>
                ),
              },
              { key: "leaderName", label: "Bang chủ" },
              { key: "lvl", label: "Cấp", align: "right" },
              { key: "fund", label: "Quỹ", align: "right", render: (c) => formatNumber(c.fund) },
              {
                key: "members",
                label: "Thành viên",
                align: "right",
                render: (c) => `${c.members}/${c.maxMember}`,
              },
              { key: "online", label: "Đang online", align: "right" },
              { key: "slogan", label: "Khẩu hiệu", wrap: true },
            ]}
          />
        </PageCard>
      </MDBox>
      <Footer />
      {selected && (
        <ClanDetailDialog
          clanId={selected}
          onClose={() => setSelected(null)}
          onChanged={() => list.reload(true)}
        />
      )}
    </DashboardLayout>
  );
}

export default Clans;
